import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import useToast from "../../../util/useToast";
import {editorConfigSelector, rxHideCarEditor, rxSubmitCarEditor, selectAllCars} from "../../../slices/carsSlice";
import {useEffect, useState} from "react";
import {MODE_EDIT} from "../../../codes";
import {createCar, updateCar} from "../../../api/ApiCars";
import {DEFAULT_COLOR} from "../../../common/ColorBox";
import {getOrDefault} from "../../../util/util";
import useNetwork from "../../../util/useNetwork";
import useValidator from "../../../util/useValidator";

const useCarEditor = () => {
  const dispatch = useDispatch();

  const {t} = useTranslation("cars");
  const {showToastSuccess} = useToast();
  const {handleNetworkError} = useNetwork();
  const validate = useValidator();

  const {show, mode, car} = useSelector(editorConfigSelector);
  const cars = useSelector(selectAllCars);

  const [editedCar, setEditedCar] = useState({errors: {}, name: ""});

  const hideCarEditor = () => dispatch(rxHideCarEditor());

  useEffect(() => {
    if (!car || !show) {
      return;
    }

    setEditedCar({
      errors: {},
      name: getOrDefault(car.name, ""),
      odometerUnit: getOrDefault(car.odometerUnit, "km"),
      quantityUnit: getOrDefault(car.quantityUnit, "L"),
      consoUnit: getOrDefault(car.consoUnit, "L/100 km"),
      default: getOrDefault(car.default, false),
      active: getOrDefault(car.active, true),
      color: getOrDefault(car.color, DEFAULT_COLOR)
    });
  }, [show, car]);

  const getErrors = values => {
    const errors = {};

    if (!values.name.trim().length) {
      errors.name = "car_editor_error_name_must_be_filled";
    } else if (cars.find(a => a !== car && a.name.toLowerCase() === values.name.trim().toLowerCase())) {
      errors.name = "car_editor_error_name_already_taken";
    }

    return errors;
  };

  const submit = e => {
    e.preventDefault();

    const values = {
      name: editedCar.name,
      odometerUnit: editedCar.odometerUnit,
      quantityUnit: editedCar.quantityUnit,
      consoUnit: editedCar.consoUnit,
      default: editedCar.default,
      active: editedCar.active,
      color: editedCar.color
    };

    return validate(getErrors(values))
      .then(() => {
        if (mode === MODE_EDIT) {
          return updateCar(car.id, values);
        } else {
          return createCar(values);
        }
      })
      .then(result => dispatch(rxSubmitCarEditor(result)))
      .then(() => {
        showToastSuccess(t(mode === MODE_EDIT ? "car_edited" : "car_added"));

        hideCarEditor();
      })
      .catch(error => {
        handleNetworkError(error, {
          400: err => setEditedCar(prev => ({...prev, errors: err.response.data}))
        });
      });
  };

  const updateEditedCar = field => event => {
    const values = {};

    switch (field) {
      case "default":
        values.default = event;

        if (values.default) {
          values.active = true;
        }

        break;
      case "color":
      case "active":
      case "odometerUnit":
      case "quantityUnit":
      case "consoUnit":
        values[field] = event;
        break;
      default:
        values[field] = event.target.value;
        break;
    }

    setEditedCar(prev => ({...prev, ...values}));
  };

  return {
    show,
    t,
    mode,
    hideCarEditor,
    car,
    editedCar,
    submit,
    updateEditedCar
  };
};

export default useCarEditor;
